import React, { useContext, useEffect, useState } from "react";
import { Button, Col, Row, Label, Input } from "reactstrap";
import { DesktopContainer } from "../layouts";
import _ from "lodash";
import { CustomerTypeahead, ServiceThreadSpinner } from ".";
import { api, UserContext } from "../utils";
import Select from "react-select";
import { toast } from "react-toastify";

export default function CustomerCard(props) {
   const customStyles = {
      menuList: (provided, state) => ({
         ...provided,
         maxHeight: "200px", // Adjust this value to control the height of the scrollable area
         overflowY: "auto",
      }),
   };
   const { currentUser } = useContext(UserContext);
   const [assignedSites, setAssignedSites] = useState([]);
   const [assignedSitesFull, setAssignedSitesFull] = useState([]);
   const [consignedCustomers, setConsignedCustomers] = useState("");
   const [customer, setCustomer] = useState();
   const [customerSearchType, setCustomerSearchType] = useState("showAll");
   const [initialized, setInitialized] = useState(false);
   const [loaded, setLoaded] = useState(false);
   const [loading, setLoading] = useState(false);
   const [numOfConsignedDays, setNumOfConsignedDays] = useState("60");
   const [refresh, setRefresh] = useState(false);
   const [selectedAssignedLocation, setSelectedAssignedLocation] = useState(null);
   const [selectedUnassignedLocation, setSelectedUnassignedLocation] = useState(null);
   const [stockStatusType, setStockStatusType] = useState("onHand");
   const [unassignedSites, setUnassignedSites] = useState([]);

   useEffect(() => {
      document.title = `Consigned Inventory Shipments`;
      if (!initialized && currentUser) {
         if (currentUser.isConsignedCustomerRole) {
            setInitialized(true);
         }
         getAllCustomers();
      }
   }, [initialized, loaded, currentUser]);

   useEffect(() => {
      if (!customer?.value) {
         return;
      }
      getAllLocations();
      getCustomer();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [customer, refresh]);

   useEffect(() => {
      getAllCustomers();
   }, [refresh]);

   function deleteCustomerLocation() {
      if (!selectedAssignedLocation?.value) {
         toast.error("Please select a location to remove");
         return;
      }
      setLoading(true);
      let site = _.find(assignedSitesFull, { customerId: selectedAssignedLocation.value });
      let payload = {
         id: site.customerLocationId
      };
      api
         .post("Customer/DeleteCustomerLocation", payload)
         .then((response) => {
            if (response && response.data.success) {
               toast.success("Location successfully removed.");
            } else {
               toast.error(response.data.message);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setLoading(false));

      setSelectedAssignedLocation(null);
      setRefresh(!refresh);
   }

   function getAllCustomers() {
      api
         .fetch(`Customer/LoadCISDropdownsCard`)
         .then((response) => {
            if (response.data.success) {
               setConsignedCustomers(
                  _.concat(
                     [{ value: "", label: "No Restriction" }],
                     _.map(response.data.consignedCustomers, (cst) => {
                        return {
                           value: cst.customerId,
                           label: cst.customerNumName,
                        };
                     })
                  )
               );
            }
         })
         .catch(api.catchHandler)
         .finally(() => setInitialized(true), setLoaded(true));
   }

   function getAllLocations() {
      api
         .fetch(`Customer/LoadAllLocations/${customer?.value}`)
         .then((response) => {
            if (response && response.data.success) {
               setUnassignedSites(_.map(response.data.unassignedSites, (x) => {
                  return { value: x.siteId, label: `${x.siteId} | ${x.siteDescription}` };
               }));
               setAssignedSites(_.map(response.data.assignedSites, (x) => {
                  return { value: x.siteId, label: `${x.siteId} | ${x.siteDescription}` }
               }));
               setAssignedSitesFull(response.data.assignedSites);
            }
         })
         .catch(api.catchHandler);
   }

   function getCustomer() {
      if (!customer?.value) {
         return;
      }
      let payload = {
         customerId: customer.value
      };
      api
         .post("Customer/GetCustomers", payload)
         .then((response) => {
            if (response && response.data.success) {
               setNumOfConsignedDays(response.data.onHoldCustomers[0].consignedNumOfDaysDue);
               if (response.data.onHoldCustomers[0].checkOnHandInventory === false) {
                  setStockStatusType("quantityTaken");
               } else {
                  setStockStatusType("onHand");
               }
            } else {
               toast.error("Failed to fetch cutomer info");
            }
         })
         .catch(api.catchHandler);
   }

   function handleCustomerTypeChange(e) {
      setCustomer(null);
      setAssignedSites([]);
      setUnassignedSites([]);
      setCustomerSearchType(e);
   }

   function saveCustomerCard() {
      if (!customer?.value) {
         toast.error("Please select a customer.");
         return;
      }

      if (!_.trim(numOfConsignedDays) || !Number(numOfConsignedDays)) {
         toast.error("Please enter a numerical amount for consigned number of days.");
         return;
      }

      setLoading(true)
      let payload = {
         customerId: customer.value,
         consignedNumOfDaysDue: parseInt(numOfConsignedDays),
         checkOnHandInventory: stockStatusType === "onHand"
      };
      api
         .post("Customer/SaveCustomerCard", payload)
         .then((response) => {
            if (response && response.data.success) {
               toast.success(`Consigned Number of Days saved successfully for customer: ${customer.value}`);
            } else {
               toast.error(response.data.message);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setLoading(false));
   }

   function setCustomerLocation() {
      if (!selectedUnassignedLocation?.value) {
         toast.error("Please select a location to add");
         return;
      }
      setLoading(true);
      let payload = {
         customerId: customer.value,
         siteId: selectedUnassignedLocation.value
      };
      api
         .post("Customer/SetCustomerLocation", payload)
         .then((response) => {
            if (response && response.data.success) {
               toast.success("Location successfully assigned.");
            } else {
               toast.error(response.data.message);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setLoading(false));

      setSelectedUnassignedLocation(null);
      setRefresh(!refresh);
   }

   return (
      <DesktopContainer screenName="Consigned Inventory Shipment Inquiry">
         <ServiceThreadSpinner on={!loaded}>
            <div className="filter-header mb-3">
               <Row className="align-items-center my-3">
                  <Col xs="1"></Col>
                  <Col xs={1}>
                     <h5 children="Customer:" style={{ color: "white", fontSize: "1.25rem" }} />
                  </Col>
                  <Col xs={9}>
                     {customerSearchType === "withAssigned" ? (
                        <Select
                           filterName="CustomerId"
                           options={consignedCustomers}
                           value={customer}
                           onChange={(e) => setCustomer(e)}
                        />
                     ) : (
                        <CustomerTypeahead value={customer} onChange={(e) => setCustomer(e)} />
                     )}
                  </Col>
               </Row>
               <Row className="align-items-center my-3">
                  <Col xs="1"></Col>
                  <Col xs="3">
                     <Label style={{ color: "white", fontSize: "1.25rem" }}>
                        <Input
                           type="radio"
                           name="with-assigned"
                           value="withAssigned"
                           className="me-1"
                           checked={customerSearchType === "withAssigned"}
                           onChange={(e) => handleCustomerTypeChange(e.target.value)}
                        />
                        With Assigned Sites
                     </Label>
                  </Col>
                  <Col xs="6"></Col>
               </Row>
               <Row className="align-items-center my-3">
                  <Col xs="1"></Col>
                  <Col xs="3">
                     <Label style={{ color: "white", fontSize: "1.25rem" }}>
                        <Input
                           type="radio"
                           name="show-all"
                           value="showAll"
                           className="me-1"
                           checked={customerSearchType === "showAll"}
                           onChange={(e) => handleCustomerTypeChange(e.target.value)}
                        />
                        Show All Customers
                     </Label>
                  </Col>
                  <Col xs="8"></Col>
               </Row>
               <Row className="align-items-center my-2">
                  <Col xs="1"></Col>
                  <Col xs="4">
                     <h5 style={{ color: "white", fontSize: "1.25rem" }}>Unassigned Sites</h5>
                     <Select
                        classNamePrefix="serviceThreadSelect"
                        isMulti={false}
                        options={unassignedSites}
                        styles={customStyles}
                        menuIsOpen={true}
                        onChange={(e) => setSelectedUnassignedLocation(e)}
                        value={selectedUnassignedLocation}
                     />
                  </Col>
                  <Col xs="2">
                     <Row style={{ height: "120px" }}></Row>
                     <Row className="d-flex justify-content-center my-3">
                        <Col xs="3">
                           <p
                              style={{ color: "white", fontSize: "2rem", cursor: "pointer" }}
                              onClick={() => setCustomerLocation()}
                           >
                              &gt;
                           </p>
                        </Col>
                     </Row>
                     <Row className="d-flex justify-content-center my-3">
                        <Col xs="3">
                           <p
                              style={{ color: "white", fontSize: "2rem", cursor: "pointer" }}
                              onClick={() => deleteCustomerLocation()}
                           >
                              &lt;
                           </p>
                        </Col>
                     </Row>
                  </Col>
                  <Col xs="4" style={{ justifyContent: "center" }}>
                     <h5 style={{ color: "white", fontSize: "1.25rem" }}>Assigned Sites</h5>
                     <Select
                        classNamePrefix="serviceThreadSelect"
                        isMulti={false}
                        options={assignedSites}
                        styles={customStyles}
                        menuIsOpen={true}
                        onChange={(e) => setSelectedAssignedLocation(e)}
                        value={selectedAssignedLocation}
                     />
                  </Col>
                  <Col xs="1"></Col>
               </Row>
               <Row style={{ height: "150px" }}></Row>
               <Row className="align-items-center my-2">
                  <Col xs="1"></Col>
                  <Col xs="3">
                     <p style={{ color: "white", fontSize: "1.25rem", margin: "0px" }}>Consigned Number Of Days Due:</p>
                  </Col>
                  <Col xs="4">
                     <Input
                        type="text"
                        value={numOfConsignedDays}
                        onChange={(e) => setNumOfConsignedDays(e.target.value)}
                     />
                  </Col>
               </Row>
               <Row className="align-items-center mt-5">
                  <Col xs="1"></Col>
                  <Col xs="3">
                     <p style={{ color: "white", fontSize: "1.25rem", margin: "0px" }}>Stock Status Type:</p>
                  </Col>
                  <Col xs="4">
                     <Label style={{ color: "white", fontSize: "1.25rem" }}>
                        <Input
                           type="radio"
                           name="on-hand"
                           value="onHand"
                           className="me-1"
                           checked={stockStatusType === "onHand"}
                           onChange={(e) => setStockStatusType(e.target.value)}
                        />
                        Check On Hand Inventory
                     </Label>
                  </Col>
               </Row>
               <Row className="align-items-center my-2">
                  <Col xs="4"></Col>
                  <Col xs="4">
                     <Label style={{ color: "white", fontSize: "1.25rem" }}>
                        <Input
                           type="radio"
                           name="quantity-taken"
                           value="quantityTaken"
                           className="me-1"
                           checked={stockStatusType === "quantityTaken"}
                           onChange={(e) => setStockStatusType(e.target.value)}
                        />
                        Check Quantity Taken
                     </Label>
                  </Col>
               </Row>
               <Row className="align-items-center my-2">
                  <Col xs="1"></Col>
                  <Col xs="2">
                     <Button className="shredButton" disabled={loading} onClick={() => saveCustomerCard()} >
                        SAVE
                     </Button>
                  </Col>
               </Row>
            </div>
         </ServiceThreadSpinner>
      </DesktopContainer>
   );
}
